// API service for Ordio frontend
const rawBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000').trim();

export const getApiBase = () => {
  try {
    let base = rawBase.replace(/\/$/, '');
    if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:' && base.startsWith('http://')) {
      base = base.replace('http://', 'https://');
    }
    return base;
  } catch (_) {
    return rawBase.replace(/\/$/, '');
  }
};

export const buildUrl = (path = '') => {
  const base = getApiBase();
  if (!path) return base;
  const origin = base.endsWith('/') ? base : `${base}/`;
  const relative = String(path).replace(/^\/+/, '');
  return `${origin}${relative}`;
};

const API_BASE_URL = getApiBase();

// ================================
// AUTHENTICATION
// ================================

export const loginUser = async (email, password) => {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      username: email,
      password: password,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Login failed');
  }

  return response.json();
};

export const registerUser = async (userData) => {
  const response = await fetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Registration failed');
  }

  return response.json();
};

// ================================
// S3 FILE MANAGEMENT (NEW SYSTEM)
// ================================

export const getUploadUrl = async (filename, contentType) => {
  const token = localStorage.getItem('access_token');
  
  // Ensure we have a content type for signature matching
  const finalContentType = contentType || 'application/octet-stream';
  
  const response = await fetch(`${API_BASE_URL}/files/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      filename: filename,
      content_type: finalContentType,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get upload URL');
  }

  const result = await response.json();
  
  // Return the content type so upload function can use the exact same one
  return {
    ...result,
    content_type: finalContentType
  };
};

export const uploadFileToS3 = async (presignedUrl, file, contentType) => {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      // MUST match the ServerSideEncryption parameter in presigned URL
      'x-amz-server-side-encryption': 'aws:kms',
      // MUST match the ContentType parameter in presigned URL
      'Content-Type': contentType,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to upload file to S3');
  }

  return response;
};

// XHR variant with progress callback
export const uploadFileToS3WithProgress = (presignedUrl, file, contentType, onProgress) =>
  new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('x-amz-server-side-encryption', 'aws:kms');
      xhr.setRequestHeader('Content-Type', contentType);

      xhr.upload.onprogress = (evt) => {
        if (!onProgress || !evt.lengthComputable) return;
        const percent = Math.round((evt.loaded / evt.total) * 100);
        onProgress(percent);
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress && onProgress(100);
          resolve({ ok: true, status: xhr.status });
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error during S3 upload'));
      xhr.send(file);
    } catch (e) {
      reject(e);
    }
  });

export const confirmUpload = async (uploadId, fileSize) => {
  const token = localStorage.getItem('access_token');
  
  // Send file_size as query parameter to match backend expectation
  const url = new URL(`${API_BASE_URL}/files/${uploadId}/confirm`);
  url.searchParams.append('file_size', fileSize);
  
  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    // No body needed since file_size is in query params
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to confirm upload');
  }

  return response.json();
};

// ================================
// MULTIPART UPLOAD HELPERS
// ================================

export const multipartInitiate = async (filename, contentType, sizeBytes) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/files/multipart/initiate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ filename, content_type: contentType || 'application/octet-stream', size_bytes: sizeBytes || null }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to initiate multipart upload');
  }
  return response.json();
};

export const multipartPresignPart = async (key, s3UploadId, partNumber) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/files/multipart/presign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ key, s3_upload_id: s3UploadId, part_number: partNumber }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to presign part ${partNumber}`);
  }
  return response.json();
};

export const multipartComplete = async ({ key, s3UploadId, parts, uploadId, fileSize }) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/files/multipart/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      key,
      s3_upload_id: s3UploadId,
      parts: parts.map(p => ({ part_number: p.partNumber, e_tag: p.eTag })),
      upload_id: uploadId,
      file_size: fileSize,
    }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to complete multipart upload');
  }
  return response.json();
};

export const multipartAbort = async (key, s3UploadId) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/files/multipart/abort`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ key, s3_upload_id: s3UploadId }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to abort multipart upload');
  }
  return response.json();
};

// Internal: upload a blob using XHR and report fine-grained progress
const uploadBlobWithProgress = (url, blob, onProgress) =>
  new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);
      // For upload_part, do NOT add headers like Content-Type or SSE unless included in the signature
      xhr.upload.onprogress = (evt) => {
        if (onProgress && evt.lengthComputable) onProgress(evt.loaded);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = (xhr.getResponseHeader('ETag') || '').replace(/\"/g, '').replace(/"/g, '');
          resolve({ ok: true, status: xhr.status, eTag: etag });
        } else {
          reject(new Error(`Part upload failed with status ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error('Network error during part upload'));
      xhr.send(blob);
    } catch (e) {
      reject(e);
    }
  });

// Upload a large file via multipart directly to S3 using presigned part URLs
export const uploadLargeFileMultipart = async (file, { partSizeBytes = 16 * 1024 * 1024, concurrency = 6 } = {}, onProgress = null) => {
  const init = await multipartInitiate(file.name, file.type, file.size);
  const totalParts = Math.ceil(file.size / partSizeBytes);
  const parts = new Array(totalParts);
  const perPartLoaded = new Array(totalParts).fill(0);
  let totalUploadedBytes = 0;

  const updateProgress = () => {
    if (!onProgress) return;
    const percent = Math.floor((totalUploadedBytes / file.size) * 100);
    onProgress(percent);
  };

  let nextPart = 1;
  const worker = async () => {
    while (nextPart <= totalParts) {
      const partNumber = nextPart++;
      const start = (partNumber - 1) * partSizeBytes;
      const end = Math.min(start + partSizeBytes, file.size);
      const blob = file.slice(start, end);

      const { url } = await multipartPresignPart(init.key, init.s3_upload_id, partNumber);
      const res = await uploadBlobWithProgress(url, blob, (loaded) => {
        const idx = partNumber - 1;
        const delta = Math.max(0, loaded - perPartLoaded[idx]);
        if (delta) {
          perPartLoaded[idx] += delta;
          totalUploadedBytes += delta;
          updateProgress();
        }
      });
      const eTag = res.eTag;
      parts[partNumber - 1] = { partNumber, eTag };
    }
  };

  const workers = Array.from({ length: Math.min(concurrency, totalParts) }, () => worker());
  await Promise.all(workers);

  const ordered = parts.sort((a, b) => a.partNumber - b.partNumber);
  await multipartComplete({ key: init.key, s3UploadId: init.s3_upload_id, parts: ordered, uploadId: init.upload_id, fileSize: file.size });
  onProgress && onProgress(100);
  return { key: init.key, upload_id: init.upload_id };
};

export const getUserFiles = async () => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/files`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get user files');
  }

  return response.json();
};

export const deleteFile = async (fileKey) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/files/${encodeURIComponent(fileKey)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete file');
  }

  return response.json();
};

export const renameFile = async (uploadId, newFilename) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/files/rename`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ upload_id: uploadId, new_filename: newFilename }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to rename file');
  }
  return response.json();
};

export const updateVirtualPath = async (uploadId, virtualPath) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/files/update-virtual-path`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ upload_id: uploadId, virtual_path: virtualPath }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update virtual path');
  }
  return response.json();
};

export const getFileVersions = async (uploadId) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/files/${uploadId}/versions`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get file versions');
  }
  return response.json();
};

export const revertFilename = async (uploadId, versionIndex) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/files/revert-filename`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ upload_id: uploadId, version_index: versionIndex }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to revert filename');
  }
  return response.json();
};

export const bulkDeleteFiles = async (fileKeys) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/files/bulk-delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      file_keys: fileKeys,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete files');
  }

  return response.json();
};

export const getDownloadUrl = async (fileKey) => {
  const token = localStorage.getItem('access_token');
  
  const safeKey = encodeURIComponent(fileKey);
  const response = await fetch(`${API_BASE_URL}/files/${safeKey}/download-url`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get download URL');
  }

  return response.json();
};

// ================================
// FILE SHARING
// ================================

export const issueShareLink = async ({ fileKey, recipientEmail = null, recipientName = null, message = null, expiresMinutes = 10080, maxDownloads = 1, passcode = null }) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/shares/issue`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      file_key: fileKey,
      recipient_email: recipientEmail,
      recipient_name: recipientName,
      message,
      expires_minutes: expiresMinutes,
      max_downloads: maxDownloads,
      passcode,
    }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to issue share link');
  }
  return response.json(); // { share_url, token }
};

export const sendShareEmail = async ({ token, recipientEmail, recipientName = null, message = null }) => {
  const response = await fetch(`${API_BASE_URL}/shares/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, recipient_email: recipientEmail, recipient_name: recipientName, message })
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || 'Failed to send share email');
  }
  return response.json(); // { emailed: true }
};

// Complete S3 upload flow
export const uploadFileComplete = async (file, onProgress = null) => {
  try {
    // Step 1: Get presigned upload URL
    const uploadData = await getUploadUrl(file.name, file.type);
    
    if (onProgress) onProgress(25, 'Getting upload URL...');
    
    // Step 2: Upload file to S3
    await uploadFileToS3(uploadData.presigned_url, file, uploadData.content_type);
    
    if (onProgress) onProgress(75, 'Uploading to S3...');
    
    // Step 3: Confirm upload
    await confirmUpload(uploadData.upload_id, file.size);
    
    if (onProgress) onProgress(100, 'Upload completed!');
    
    return {
      upload_id: uploadData.upload_id,
      success: true,
      message: 'File uploaded successfully',
    };
    
  } catch (error) {
    throw new Error(`Upload failed: ${error.message}`);
  }
};

// ================================
// AI SERVICES
// ================================

export const askAI = async (question, files = null) => {
  const token = localStorage.getItem('access_token');
  
  const formData = new FormData();
  if (question) formData.append('question', question);
  
  if (files) {
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
  }

  const response = await fetch(`${API_BASE_URL}/ai/ask`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'AI request failed');
  }

  return response.json();
};

export const getContextualChatResponse = async (question) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/contextual-chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      message: question,
      include_files: true,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Contextual chat failed');
  }

  return response.json();
};

export const analyzeFiles = async (uploadIds) => {
  const token = localStorage.getItem('access_token');

  // Simple single-flight guard: avoid duplicate concurrent calls with same IDs
  const key = Array.isArray(uploadIds) ? uploadIds.slice().sort().join(',') : String(uploadIds);
  if (!window.__analyze_inflight__) window.__analyze_inflight__ = new Map();
  if (window.__analyze_inflight__.has(key)) return window.__analyze_inflight__.get(key);

  const exec = async () => {
    const maxAttempts = 5;
    const baseDelayMs = 400;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const response = await fetch(`${API_BASE_URL}/ai/analyze-files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ upload_ids: uploadIds }),
      });

      if (response.ok) {
        const data = await response.json();
        return data;
      }

      // Handle 429/503 with exponential backoff + jitter
      if (response.status === 429 || response.status === 503) {
        const jitter = Math.random() * 200;
        const delay = Math.min(8000, Math.round((2 ** (attempt - 1)) * baseDelayMs + jitter));
        await new Promise(r => setTimeout(r, delay));
        continue;
      }

      // Other errors: surface message
      let message = 'File analysis failed';
      try { const err = await response.json(); message = err.detail || message; } catch (_) {}
      throw new Error(message);
    }

    throw new Error('File analysis is busy. Please try again shortly.');
  };

  const promise = exec().finally(() => {
    window.__analyze_inflight__ && window.__analyze_inflight__.delete(key);
  });
  window.__analyze_inflight__.set(key, promise);
  return promise;
};

export const getUserContext = async () => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/user-context`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get user context');
  }

  return response.json();
};

// ================================
// INTEGRATIONS (Cloud Providers)
// ================================

export const getIntegrationStatus = async () => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/integrations/cloud/status`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    let msg = 'Failed to get integration status';
    try { const err = await response.json(); msg = err.detail || msg; } catch (_) {}
    throw new Error(msg);
  }
  return response.json();
};

export const getProviderAuthUrl = async (provider) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/integrations/cloud/${provider}/connect`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to start OAuth');
  }
  return response.json(); // { auth_url }
};

export const disconnectProvider = async (provider) => {
  const token = localStorage.getItem('access_token');
  const url = new URL(`${API_BASE_URL}/integrations/cloud/disconnect`);
  url.searchParams.set('provider', provider);
  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to disconnect');
  }
  return response.json();
};

export const listGoogleDriveFiles = async () => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/integrations/cloud/google/drive/list`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    let msg = 'Failed to list Google Drive files';
    try { const err = await response.json(); msg = err.detail || msg; } catch (_) {}
    throw new Error(msg);
  }
  return response.json();
};

export const listGoogleDriveChildren = async ({ parentId = 'root', pageToken = null, pageSize = 100 } = {}) => {
  const token = localStorage.getItem('access_token');
  const url = new URL(`${API_BASE_URL}/integrations/cloud/google/drive/children`);
  url.searchParams.set('parent_id', parentId);
  if (pageToken) url.searchParams.set('page_token', pageToken);
  url.searchParams.set('page_size', pageSize);
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!response.ok) {
    let msg = 'Failed to list folder contents';
    try { const err = await response.json(); msg = err.detail || msg; } catch (_) {}
    throw new Error(msg);
  }
  return response.json(); // { nextPageToken, files: [...] }
};

export const updateFileTags = async (uploadId, tags) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/update-file-tags`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      upload_id: uploadId,
      tags: tags,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update file tags');
  }

  return response.json();
};

export const getFileAnalysis = async (uploadId) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/file-analysis/${uploadId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get file analysis');
  }

  return response.json();
};

export const suggestFileSorting = async (context = null) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/suggest-sorting`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      context: context,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get sorting suggestions');
  }

  return response.json();
};

export const suggestFileTags = async (uploadId, additionalContext = null) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/suggest-tags`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      upload_id: uploadId,
      additional_context: additionalContext,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get tag suggestions');
  }

  return response.json();
};

export const searchFilesContextually = async (query, filters = null) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/search-files`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      query: query,
      filters: filters,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Contextual search failed');
  }

  return response.json();
};

export const getFileOrganizationInsights = async () => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/file-organization-insights`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get organization insights');
  }

  return response.json();
};

export const bulkTagSuggestions = async (uploadIds) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/bulk-tag-suggestions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      upload_ids: uploadIds,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get bulk tag suggestions');
  }

  return response.json();
};

export const getSmartFileRecommendations = async () => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/smart-file-recommendations`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get file recommendations');
  }

  return response.json();
};

// ================================
// TEMPLATES & TAGS MANAGEMENT
// ================================

export const createTag = async (tagData) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/templates-tags/tags`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(tagData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create tag');
  }

  return response.json();
};

// ================================
// ACCOUNT MANAGEMENT
// ================================

export const deleteAccount = async ({ cancelAtPeriodEnd = true, reason = null, hardDelete = false } = {}) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/account/delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ cancel_at_period_end: cancelAtPeriodEnd, reason, hard_delete: hardDelete }),
  });
  if (!response.ok) {
    let msg = 'Failed to delete account';
    try { const err = await response.json(); msg = err.detail || msg; } catch (_) {}
    throw new Error(msg);
  }
  return response.json();
};

export const getUserTags = async () => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/templates-tags/tags`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get tags');
  }

  return response.json();
};

export const updateTag = async (tagId, updates) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/templates-tags/tags/${tagId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update tag');
  }

  return response.json();
};

export const deleteTag = async (tagId) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/templates-tags/tags/${tagId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete tag');
  }

  return response.json();
};

export const addTagToFiles = async (tagId, fileIds) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/templates-tags/tags/${tagId}/files`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      file_ids: fileIds,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to add tag to files');
  }

  return response.json();
};

export const removeTagFromFile = async (tagId, fileId) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/templates-tags/tags/${tagId}/files/${fileId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to remove tag from file');
  }

  return response.json();
};

export const getFilesByTag = async (tagId) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/templates-tags/tags/${tagId}/files`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get files by tag');
  }

  return response.json();
};

// Templates
export const createSortingTemplate = async (templateData) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/templates-tags/templates`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(templateData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create template');
  }

  return response.json();
};

export const getSortingTemplates = async () => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/templates-tags/templates`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get templates');
  }

  return response.json();
};

export const updateSortingTemplate = async (templateId, updates) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/templates-tags/templates/${templateId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update template');
  }

  return response.json();
};

export const deleteSortingTemplate = async (templateId) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/templates-tags/templates/${templateId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete template');
  }

  return response.json();
};

export const applySortingTemplate = async (templateId, fileIds = null) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/templates-tags/templates/${templateId}/apply`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      template_id: templateId,
      file_ids: fileIds,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to apply template');
  }

  return response.json();
};

// AI Template & Tag Suggestions
export const getAITemplateSuggestion = async (context = null) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/suggest-template`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      context: context,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get AI template suggestion');
  }

  return response.json();
};

export const sendSuggestionFeedback = async (suggestionType, action, payload = {}, reason = null) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_BASE_URL}/ai/suggestion-feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      suggestion_type: suggestionType,
      action,
      payload,
      reason,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to record suggestion feedback');
  }

  return response.json();
};

export const getAITagSuggestions = async (fileIds) => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/suggest-tags-for-files`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(fileIds),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get AI tag suggestions');
  }

  return response.json();
};

export const getOrganizationRecommendations = async () => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/organization-recommendations`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get organization recommendations');
  }

  return response.json();
};

// ================================
// DEBUG ENDPOINTS
// ================================

export const debugFileVisibility = async () => {
  const token = localStorage.getItem('access_token');
  
  const response = await fetch(`${API_BASE_URL}/ai/debug/file-visibility`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Debug request failed');
  }

  return response.json();
};

